import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { 
  Database, 
  Export, 
  Trash, 
  ArrowCounterClockwise, 
  Archive,
  Fire,
  Warning,
  CheckCircle,
  XCircle,
  Spinner,
  Download,
  Info,
  ShieldCheck,
  MagnifyingGlass,
  Wrench,
  ClockCounterClockwise,
  User,
  Calendar
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Normalize status for display - PURGED and TRUE_PURGED show as ARCHIVED
function normalizeStatusForDisplay(status) {
  if (status === "PURGED" || status === "TRUE_PURGED") {
    return "ARCHIVED";
  }
  return status;
}

// Get human-readable status explanation
function getStatusExplanation(rawStatus, hasData) {
  const explanations = {
    active: "Full functionality. Login allowed. Can be deleted to quarantine.",
    DELETED: "Login blocked. In quarantine. Can be restored or archived permanently.",
    DELETING: "Delete operation in progress...",
    DELETING_FAILED: "Delete operation failed. Requires admin attention.",
    ARCHIVED: "Permanently disabled. Export retained for historical record. Login blocked.",
    PURGED: "Legacy status. Should be normalized to ARCHIVED via Repair Status.",
    TRUE_PURGED: "Data destroyed. This is a dead-end state. Use Repair Status to convert to ARCHIVED for record keeping."
  };
  return explanations[rawStatus] || "Unknown status";
}

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

// Simplified confirmation modal - just type "DELETE" or "TRUE_PURGE"
function ConfirmModal({ 
  title, 
  message, 
  warningLevel, 
  confirmText, 
  onConfirm, 
  onCancel, 
  isProcessing, 
  processingText,
  children 
}) {
  const [inputText, setInputText] = useState("");
  const canConfirm = inputText === confirmText;
  
  const warningStyles = {
    danger: "bg-red-500/10 border-red-500/30 text-red-400",
    warning: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    info: "bg-blue-500/10 border-blue-500/30 text-blue-400",
  };
  
  const buttonStyles = {
    danger: "bg-red-600 hover:bg-red-700",
    warning: "bg-amber-600 hover:bg-amber-700",
    info: "bg-blue-600 hover:bg-blue-700",
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full mx-4">
        <div className={`flex items-center gap-3 mb-4 ${warningLevel === 'danger' ? 'text-red-400' : warningLevel === 'warning' ? 'text-amber-400' : 'text-blue-400'}`}>
          {warningLevel === 'danger' ? <Fire size={24} /> : warningLevel === 'warning' ? <Warning size={24} /> : <Info size={24} />}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        
        <p className="text-zinc-300 mb-4">{message}</p>
        
        {children}
        
        <div className={`${warningStyles[warningLevel]} border rounded p-3 mb-4`}>
          <p className="text-sm">
            {warningLevel === 'danger' 
              ? "This action is PERMANENT and cannot be undone."
              : warningLevel === 'warning'
              ? "Please confirm this action."
              : "Confirm to proceed."
            }
          </p>
        </div>
        
        <div className="mb-4">
          <label className="text-sm text-zinc-400 block mb-2">
            Type <span className="font-mono bg-zinc-800 px-1.5 py-0.5 rounded">{confirmText}</span> to confirm:
          </label>
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white focus:border-blue-500 focus:outline-none"
            placeholder={confirmText}
            disabled={isProcessing}
            autoFocus
            data-testid="confirm-input"
          />
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canConfirm || isProcessing}
            className={`flex-1 ${buttonStyles[warningLevel]} text-white disabled:opacity-50`}
            data-testid="confirm-btn"
          >
            {isProcessing ? (
              <>
                <Spinner size={16} className="mr-2 animate-spin" />
                {processingText || 'Processing...'}
              </>
            ) : (
              title
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Select export modal for restore
function SelectExportModal({ exports, onSelect, onCancel, isProcessing }) {
  const [selected, setSelected] = useState(exports[0]?.export_id || "");
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <ArrowCounterClockwise size={20} className="text-emerald-400" />
          Select Export to Restore
        </h3>
        
        <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
          {exports.map((exp) => (
            <label
              key={exp.export_id}
              className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                selected === exp.export_id 
                  ? 'bg-emerald-500/20 border border-emerald-500/30' 
                  : 'bg-zinc-800 hover:bg-zinc-700 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="export"
                value={exp.export_id}
                checked={selected === exp.export_id}
                onChange={(e) => setSelected(e.target.value)}
                className="accent-emerald-500"
              />
              <div className="flex-1">
                <div className="text-sm text-zinc-300 font-mono">{exp.export_id.slice(0, 24)}...</div>
                <div className="text-xs text-zinc-500">
                  {formatDate(exp.created_at)} • {formatBytes(exp.size_bytes)}
                </div>
              </div>
            </label>
          ))}
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSelect(selected)}
            disabled={!selected || isProcessing}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {isProcessing ? (
              <>
                <Spinner size={16} className="mr-2 animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <ArrowCounterClockwise size={16} className="mr-2" />
                Restore
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TenantLifecycleTab({ tenant, onUpdate }) {
  const [inventory, setInventory] = useState(null);
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // System capabilities (for R2 guard)
  const [capabilities, setCapabilities] = useState(null);
  
  // Truth check state
  const [truth, setTruth] = useState(null);
  const [truthLoading, setTruthLoading] = useState(false);
  
  // Repair state
  const [repairPreview, setRepairPreview] = useState(null);
  const [repairConfirmText, setRepairConfirmText] = useState("");
  const [isRepairing, setIsRepairing] = useState(false);
  
  // Modal states
  const [deleteModal, setDeleteModal] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [archiveModal, setArchiveModal] = useState(false);
  const [truePurgeModal, setTruePurgeModal] = useState(false);
  const [showAdvancedDestroy, setShowAdvancedDestroy] = useState(false);
  
  // Processing states
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isTruePurging, setIsTruePurging] = useState(false);
  
  const tenantId = tenant?.tenant_id;
  // Get raw status and normalized display status
  const rawStatus = tenant?.status || "active";
  const displayStatus = normalizeStatusForDisplay(rawStatus);
  
  // Status checks - normalize PURGED and TRUE_PURGED for UI logic
  const isActive = !rawStatus || rawStatus === "active";
  const isDeleted = rawStatus === "DELETED" || rawStatus === "DELETING_FAILED";
  const isArchived = rawStatus === "ARCHIVED" || rawStatus === "PURGED";
  const isTruePurged = rawStatus === "TRUE_PURGED";
  const isDeadEnd = rawStatus === "TRUE_PURGED"; // Only TRUE_PURGED is dead-end, PURGED can be repaired to ARCHIVED
  const isTerminal = isArchived || isTruePurged;
  const hasLegacyPurgedStatus = rawStatus === "PURGED"; // Legacy status that needs normalization
  
  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      
      const [invResponse, expResponse, healthResponse] = await Promise.all([
        fetch(`${API}/admin/cleanup/${tenantId}/inventory`, { credentials: "include" }),
        fetch(`${API}/admin/cleanup/${tenantId}/exports`, { credentials: "include" }),
        fetch(`${API}/admin/system/health`, { credentials: "include" })
      ]);
      
      if (invResponse.ok) {
        const invData = await invResponse.json();
        setInventory(invData);
      }
      
      if (expResponse.ok) {
        const expData = await expResponse.json();
        setExports(expData.exports || []);
      }
      
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        setCapabilities(healthData.capabilities || null);
      }
    } catch (error) {
      console.error("Error fetching lifecycle data:", error);
      toast.error("Failed to load lifecycle data");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create export
  const createExport = async () => {
    try {
      setExporting(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/export`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "manual" })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Export created: ${data.export?.manifest?.totals?.total_documents || 0} documents`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create export");
      }
    } catch (error) {
      console.error("Error creating export:", error);
      toast.error("Failed to create export");
    } finally {
      setExporting(false);
    }
  };

  // Download export
  const downloadExport = async (exportId) => {
    try {
      const response = await fetch(`${API}/admin/cleanup/exports/${exportId}/download`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        window.open(data.download_url, '_blank');
      } else {
        toast.error("Failed to get download URL");
      }
    } catch (error) {
      toast.error("Failed to download export");
    }
  };

  // Initiate delete (get confirm token)
  const initiateDelete = async () => {
    try {
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/delete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: true })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDeleteModal({
          confirmToken: data.confirm_token,
          confirmString: data.required_confirmation, // "DELETE"
          preview: data.preview
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to prepare delete");
      }
    } catch (error) {
      toast.error("Failed to prepare delete");
    }
  };

  // Execute delete
  const executeDelete = async () => {
    if (!deleteModal) return;
    
    try {
      setIsDeleting(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/delete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dry_run: false,
          confirm_token: deleteModal.confirmToken,
          confirmation_string: deleteModal.confirmString
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Tenant deleted: ${data.deletion_stats?.total_deleted || 0} documents removed`);
        setDeleteModal(null);
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Delete failed");
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

  // Initiate restore
  const initiateRestore = () => {
    const completedExports = exports.filter(e => e.status === "completed");
    if (completedExports.length === 0) {
      toast.error("No completed exports available for restore");
      return;
    }
    setRestoreModal({ exports: completedExports });
  };

  // Execute restore
  const executeRestore = async (exportId) => {
    try {
      setIsRestoring(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/restore`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ export_id: exportId })
      });
      
      if (response.ok) {
        toast.success("Tenant restored successfully");
        setRestoreModal(null);
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Restore failed");
      }
    } catch (error) {
      toast.error("Restore failed");
    } finally {
      setIsRestoring(false);
    }
  };

  // Execute archive
  const executeArchive = async () => {
    try {
      setIsArchiving(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/archive`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        toast.success("Tenant archived permanently");
        setArchiveModal(false);
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Archive failed");
      }
    } catch (error) {
      toast.error("Archive failed");
    } finally {
      setIsArchiving(false);
    }
  };

  // Execute true purge
  const executeTruePurge = async () => {
    try {
      setIsTruePurging(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/true-purge`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          confirmation_string: "TRUE_PURGE",
          remove_tenant_record: false
        })
      });
      
      if (response.ok) {
        toast.success("Tenant permanently destroyed");
        setTruePurgeModal(false);
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "True purge failed");
      }
    } catch (error) {
      toast.error("True purge failed");
    } finally {
      setIsTruePurging(false);
    }
  };

  // Fetch truth diagnostic
  const fetchTruth = async () => {
    try {
      setTruthLoading(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/truth`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setTruth(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to fetch truth");
      }
    } catch (error) {
      toast.error("Failed to fetch truth");
    } finally {
      setTruthLoading(false);
    }
  };

  // Initiate repair (dry run)
  const initiateRepair = async () => {
    try {
      setIsRepairing(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/repair-status`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: true })
      });
      
      if (response.ok) {
        const data = await response.json();
        setRepairPreview(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to preview repair");
      }
    } catch (error) {
      toast.error("Failed to preview repair");
    } finally {
      setIsRepairing(false);
    }
  };

  // Execute repair
  const executeRepair = async () => {
    if (repairConfirmText !== "REPAIR") {
      toast.error("Type REPAIR to confirm");
      return;
    }
    
    try {
      setIsRepairing(true);
      const response = await fetch(`${API}/admin/cleanup/${tenantId}/repair-status`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: false, confirm: "REPAIR" })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Status repaired successfully");
        setRepairPreview(null);
        setRepairConfirmText("");
        fetchTruth();
        fetchData();
        onUpdate?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Repair failed");
      }
    } catch (error) {
      toast.error("Repair failed");
    } finally {
      setIsRepairing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-zinc-800 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  const hasExport = exports.some(e => e.status === "completed");
  
  // Needs repair if: TRUE_PURGED (dead-end), or PURGED (legacy needs normalization)
  const needsRepair = isTruePurged || hasLegacyPurgedStatus;

  return (
    <div className="space-y-6" data-testid="tenant-lifecycle-tab">
      {/* Lifecycle Decision Banner - explains current state */}
      <div className={`rounded-lg border ${
        isActive ? "bg-emerald-500/10 border-emerald-500/30" :
        isDeleted ? "bg-amber-500/10 border-amber-500/30" :
        isTruePurged ? "bg-red-500/10 border-red-500/30" :
        isArchived ? "bg-zinc-500/10 border-zinc-500/30" :
        "bg-zinc-800 border-zinc-700"
      }`}>
        {/* Main Status */}
        <div className="p-4">
          <div className="flex items-center gap-3">
            {isActive ? <CheckCircle size={24} className="text-emerald-400" /> :
             isDeleted ? <Trash size={24} className="text-amber-400" /> :
             isTruePurged ? <Fire size={24} className="text-red-400" /> :
             isArchived ? <Archive size={24} className="text-zinc-400" /> :
             <Info size={24} className="text-zinc-400" />}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className={`font-semibold ${
                  isActive ? "text-emerald-400" :
                  isDeleted ? "text-amber-400" :
                  isTruePurged ? "text-red-400" :
                  isArchived ? "text-zinc-300" :
                  "text-white"
                }`}>
                  {isActive ? "Active Tenant" :
                   isDeleted ? "Deleted (Quarantine)" :
                   isTruePurged ? "Destroyed (TRUE_PURGED)" :
                   displayStatus === "ARCHIVED" ? "Archived" :
                   displayStatus}
                </p>
                {/* Show raw status if different from display */}
                {rawStatus !== displayStatus && rawStatus !== "active" && (
                  <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30">
                    Raw: {rawStatus}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-zinc-400 mt-1">
                {getStatusExplanation(rawStatus, inventory?.totals?.total_documents > 0)}
              </p>
            </div>
          </div>
        </div>
        
        {/* Lifecycle History Context */}
        {truth && (truth.last_change_at || truth.deleted_at || truth.archived_at) && (
          <div className="border-t border-zinc-700/50 px-4 py-3 bg-black/20">
            <div className="flex flex-wrap gap-4 text-sm">
              {truth.last_change_action && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <ClockCounterClockwise size={14} />
                  <span>Last action: <span className="text-zinc-300">{truth.last_change_action.replace('tenant_', '')}</span></span>
                </div>
              )}
              {truth.last_change_at && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Calendar size={14} />
                  <span>When: <span className="text-zinc-300">{formatDate(truth.last_change_at)}</span></span>
                </div>
              )}
              {truth.last_change_by && (
                <div className="flex items-center gap-2 text-zinc-400">
                  <User size={14} />
                  <span>By: <span className="text-zinc-300">{truth.last_change_by.slice(0, 12)}...</span></span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Dead-end warning - only for TRUE_PURGED */}
        {isDeadEnd && (
          <div className="border-t border-red-500/30 px-4 py-3 bg-red-500/5">
            <div className="flex items-center gap-2 text-red-400">
              <Warning size={16} />
              <span className="font-medium">Dead-end state detected.</span>
              <span className="text-zinc-400">Use Repair Status below to convert to ARCHIVED for record keeping.</span>
            </div>
          </div>
        )}
        
        {/* Legacy PURGED status warning - not dead-end, just needs normalization */}
        {hasLegacyPurgedStatus && (
          <div className="border-t border-amber-500/30 px-4 py-3 bg-amber-500/5">
            <div className="flex items-center gap-2 text-amber-400">
              <Warning size={16} />
              <span>Legacy PURGED status. Use Repair Status to normalize to ARCHIVED.</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Truth Check Section */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <MagnifyingGlass size={20} className="text-blue-400" />
            <h3 className="font-semibold text-white">Truth Check</h3>
          </div>
          <Button
            onClick={fetchTruth}
            disabled={truthLoading}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="truth-check-btn"
          >
            {truthLoading ? (
              <>
                <Spinner size={14} className="mr-2 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <MagnifyingGlass size={14} className="mr-2" />
                Run Check
              </>
            )}
          </Button>
        </div>
        
        {truth ? (
          <div className="space-y-4">
            {/* Status Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-900 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Raw Status</p>
                <p className="text-white font-mono">{truth.raw_status}</p>
              </div>
              <div className="bg-zinc-900 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Effective Status</p>
                <p className={`font-mono ${
                  truth.status_matches_data ? "text-emerald-400" : "text-amber-400"
                }`}>
                  {truth.effective_status}
                  {!truth.status_matches_data && " (mismatch!)"}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-900 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Login Allowed</p>
                <p className={truth.login_allowed ? "text-emerald-400" : "text-red-400"}>
                  {truth.login_allowed ? "Yes" : "No (Blocked)"}
                </p>
              </div>
              <div className="bg-zinc-900 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Exports</p>
                <p className="text-white">
                  {truth.export_count} exports
                  {truth.last_export_id && (
                    <span className="text-zinc-500 text-xs ml-2">
                      (latest: {truth.last_export_id.slice(0, 12)}...)
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Counts */}
            <div className="bg-zinc-900 p-3 rounded">
              <p className="text-xs text-zinc-500 mb-2">Document Counts (Reality)</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-lg font-bold text-white">{truth.counts?.customers || 0}</div>
                  <div className="text-xs text-zinc-500">Customers</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{truth.counts?.jobs || 0}</div>
                  <div className="text-xs text-zinc-500">Jobs</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{truth.counts?.visits || 0}</div>
                  <div className="text-xs text-zinc-500">Visits</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-white">{truth.counts?.invoices || 0}</div>
                  <div className="text-xs text-zinc-500">Invoices</div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-zinc-800 text-center">
                <span className="text-zinc-400">Total: </span>
                <span className="text-white font-bold">{truth.total_documents}</span>
                <span className="text-zinc-500 ml-1">documents</span>
              </div>
            </div>
            
            {/* Warnings */}
            {truth.warnings && truth.warnings.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                <p className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                  <Warning size={16} />
                  Warnings ({truth.warnings.length})
                </p>
                <ul className="space-y-1">
                  {truth.warnings.map((warning, i) => (
                    <li key={i} className="text-sm text-zinc-300">• {warning}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Repair Button - show for ALL non-ACTIVE states that can be repaired */}
            {truth.can_repair && (
              <div className={`rounded p-4 ${
                truth.is_dead_end 
                  ? "bg-red-500/10 border border-red-500/30" 
                  : "bg-purple-500/10 border border-purple-500/30"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Wrench size={20} className={truth.is_dead_end ? "text-red-400" : "text-purple-400"} />
                    <div>
                      <h4 className={`font-semibold ${truth.is_dead_end ? "text-red-400" : "text-purple-400"}`}>
                        {truth.is_dead_end ? "Fix Dead-End State" : "Repair Available"}
                      </h4>
                      <p className="text-sm text-zinc-500">
                        {truth.is_dead_end 
                          ? "Convert to ARCHIVED to make this tenant manageable."
                          : "Status can be repaired. Only metadata will be changed."}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={initiateRepair}
                    disabled={isRepairing}
                    className={truth.is_dead_end 
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-purple-600 hover:bg-purple-700 text-white"}
                    data-testid="repair-status-btn"
                  >
                    {isRepairing ? (
                      <>
                        <Spinner size={14} className="mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <Wrench size={14} className="mr-2" />
                        {truth.is_dead_end ? "Convert to Archived" : "Repair Status"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-zinc-500">
            <Info size={32} className="mx-auto mb-2 opacity-50" />
            <p>Click Run Check to diagnose tenant state</p>
          </div>
        )}
      </div>
      
      {/* Repair Preview Modal */}
      {repairPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4 text-purple-400">
              <Wrench size={24} />
              <h3 className="text-lg font-semibold">Repair Status Preview</h3>
            </div>
            
            <div className="space-y-4 mb-4">
              <div className="bg-zinc-800 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Current Status</p>
                <p className="text-white font-mono">{repairPreview.current_status}</p>
              </div>
              
              <div className="bg-zinc-800 p-3 rounded">
                <p className="text-xs text-zinc-500 mb-1">Recommended Status</p>
                <p className="text-emerald-400 font-mono">{repairPreview.recommended_status}</p>
              </div>
              
              {repairPreview.required_changes && repairPreview.required_changes.length > 0 && (
                <div className="bg-zinc-800 p-3 rounded">
                  <p className="text-xs text-zinc-500 mb-2">Changes to Apply</p>
                  {repairPreview.required_changes.map((change, i) => (
                    <div key={i} className="text-sm text-zinc-300 mb-1">
                      <span className="text-zinc-500">{change.field}:</span>{" "}
                      <span className="text-red-400">{String(change.from)}</span>
                      {" → "}
                      <span className="text-emerald-400">{String(change.to)}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {repairPreview.warnings && repairPreview.warnings.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                  <ul className="space-y-1 text-sm text-zinc-300">
                    {repairPreview.warnings.map((w, i) => (
                      <li key={i}>• {w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            {repairPreview.needs_repair ? (
              <>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 mb-4 text-sm text-blue-400">
                  <p><strong>Safety Note:</strong></p>
                  <ul className="list-disc list-inside mt-1 text-zinc-400">
                    <li>Only status/timestamps will be modified</li>
                    <li>No documents will be deleted</li>
                    <li>This action can be reversed</li>
                  </ul>
                </div>
                
                <div className="mb-4">
                  <label className="text-sm text-zinc-400 block mb-2">
                    Type <span className="font-mono bg-zinc-800 px-1.5 py-0.5 rounded">REPAIR</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={repairConfirmText}
                    onChange={(e) => setRepairConfirmText(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="REPAIR"
                    data-testid="repair-confirm-input"
                  />
                </div>
              </>
            ) : (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 mb-4 text-emerald-400">
                No repairs needed - status matches data state.
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setRepairPreview(null);
                  setRepairConfirmText("");
                }}
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              {repairPreview.needs_repair && (
                <Button
                  onClick={executeRepair}
                  disabled={repairConfirmText !== "REPAIR" || isRepairing}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                  data-testid="repair-execute-btn"
                >
                  {isRepairing ? (
                    <>
                      <Spinner size={16} className="mr-2 animate-spin" />
                      Repairing...
                    </>
                  ) : (
                    <>
                      <Wrench size={16} className="mr-2" />
                      Apply Repair
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Database size={20} className="text-blue-400" />
            <h3 className="font-semibold text-white">Data Inventory</h3>
          </div>
          {inventory?.safe_to_delete !== undefined && (
            <Badge variant="outline" className={inventory.safe_to_delete 
              ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
              : "bg-red-500/20 text-red-400 border-red-500/30"
            }>
              {inventory.safe_to_delete ? "Safe to delete" : "Has warnings"}
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-zinc-900 rounded">
            <div className="text-2xl font-bold text-white">
              {inventory?.totals?.total_documents || 0}
            </div>
            <div className="text-xs text-zinc-500">Documents</div>
          </div>
          <div className="text-center p-3 bg-zinc-900 rounded">
            <div className="text-2xl font-bold text-white">
              {inventory?.totals?.total_collections || 0}
            </div>
            <div className="text-xs text-zinc-500">Collections</div>
          </div>
          <div className="text-center p-3 bg-zinc-900 rounded">
            <div className="text-2xl font-bold text-white">
              {inventory?.totals?.total_membership_records || 0}
            </div>
            <div className="text-xs text-zinc-500">Memberships</div>
          </div>
        </div>
        
        {/* Collections breakdown */}
        <div className="space-y-1 max-h-32 overflow-y-auto">
          {Object.entries(inventory?.collections || {})
            .filter(([_, info]) => info.count > 0)
            .slice(0, 10)
            .map(([name, info]) => (
              <div key={name} className="flex justify-between text-sm py-1 px-2 hover:bg-zinc-700/30 rounded">
                <span className="text-zinc-400">{name}</span>
                <span className="text-zinc-300">{info.count}</span>
              </div>
            ))}
        </div>
        
        {/* Integrity checks */}
        {inventory?.integrity_checks?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-zinc-700">
            <p className="text-xs text-zinc-500 mb-2">Integrity Checks</p>
            <div className="space-y-1">
              {inventory.integrity_checks.map((check, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  {check.passed 
                    ? <CheckCircle size={14} className="text-emerald-400" />
                    : <XCircle size={14} className="text-red-400" />
                  }
                  <span className={check.passed ? "text-zinc-400" : "text-red-400"}>
                    {check.message}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Exports Section */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Export size={20} className="text-emerald-400" />
            <h3 className="font-semibold text-white">Exports</h3>
            <Badge variant="outline" className="bg-zinc-700 text-zinc-300">
              {exports.length}
            </Badge>
          </div>
          {isActive && (
            <Button
              onClick={createExport}
              disabled={exporting}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="create-export-btn"
            >
              {exporting ? (
                <>
                  <Spinner size={14} className="mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Export size={14} className="mr-2" />
                  Create Export
                </>
              )}
            </Button>
          )}
        </div>
        
        {exports.length === 0 ? (
          <div className="text-center py-6 text-zinc-500">
            <Info size={32} className="mx-auto mb-2 opacity-50" />
            <p>No exports yet</p>
            {isActive && <p className="text-sm">Create an export before deleting the tenant.</p>}
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {exports.map((exp) => (
              <div
                key={exp.export_id}
                className="flex items-center justify-between p-3 bg-zinc-900 rounded"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-zinc-300">
                      {exp.export_id.slice(0, 24)}...
                    </span>
                    <Badge variant="outline" className={
                      exp.status === "completed"
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }>
                      {exp.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {formatDate(exp.created_at)} • {formatBytes(exp.size_bytes)} • {exp.reason}
                  </div>
                </div>
                {exp.status === "completed" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadExport(exp.export_id)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Download size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Lifecycle Actions */}
      <div className="space-y-3">
        {/* ACTIVE: Show Delete option */}
        {isActive && (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trash size={20} className="text-amber-400" />
                <div>
                  <h3 className="font-semibold text-amber-400">Delete Tenant (Quarantine)</h3>
                  <p className="text-sm text-zinc-500">
                    Move to quarantine. Login blocked. Can restore or archive later.
                  </p>
                </div>
              </div>
              <Button
                onClick={initiateDelete}
                disabled={!hasExport}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="delete-tenant-btn"
              >
                <Trash size={16} className="mr-2" />
                Delete
              </Button>
            </div>
            
            {!hasExport && (
              <div className="mt-3 text-sm text-amber-400 flex items-center gap-2">
                <Warning size={14} />
                Create an export before deleting
              </div>
            )}
          </div>
        )}
        
        {/* DELETED: Show Restore and Archive options */}
        {isDeleted && (
          <>
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ArrowCounterClockwise size={20} className="text-emerald-400" />
                  <div>
                    <h3 className="font-semibold text-emerald-400">Restore Tenant</h3>
                    <p className="text-sm text-zinc-500">
                      Restore data from export. Tenant becomes active again.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={initiateRestore}
                  disabled={!hasExport}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="restore-tenant-btn"
                >
                  <ArrowCounterClockwise size={16} className="mr-2" />
                  Restore
                </Button>
              </div>
            </div>
            
            <div className="bg-zinc-500/5 border border-zinc-500/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Archive size={20} className="text-zinc-400" />
                  <div>
                    <h3 className="font-semibold text-zinc-300">Archive Permanently</h3>
                    <p className="text-sm text-zinc-500">
                      Permanent disable. Export retained. Restore NOT available after this.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setArchiveModal(true)}
                  variant="outline"
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                  data-testid="archive-tenant-btn"
                >
                  <Archive size={16} className="mr-2" />
                  Archive
                </Button>
              </div>
            </div>
          </>
        )}
        
        {/* ARCHIVED (including normalized PURGED): Show historical info and TRUE_PURGED conversion warning */}
        {(isArchived && !isTruePurged) && (
          <>
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck size={20} className="text-zinc-400" />
                <div>
                  <h3 className="font-semibold text-zinc-300">Archived Tenant</h3>
                  <p className="text-sm text-zinc-500">
                    Permanently disabled. Export retained for historical record.
                  </p>
                </div>
              </div>
              
              {tenant.archive_summary && (
                <div className="bg-zinc-900 rounded p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Archived at:</span>
                    <span className="text-zinc-300">{formatDate(tenant.archived_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Original docs:</span>
                    <span className="text-zinc-300">{tenant.archive_summary.total_documents_at_delete || 0}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Advanced: Data Destruction (hidden behind toggle) */}
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Fire size={20} className="text-zinc-500" />
                  <div>
                    <h3 className="font-semibold text-zinc-400">Advanced Options</h3>
                    <p className="text-sm text-zinc-600">
                      Destructive actions for compliance/legal requirements only.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvancedDestroy(!showAdvancedDestroy)}
                  className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                >
                  {showAdvancedDestroy ? "Hide" : "Show"}
                </Button>
              </div>
              
              {showAdvancedDestroy && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="bg-red-500/5 border border-red-500/20 rounded p-4">
                    <div className="flex items-center gap-2 text-red-400 mb-3">
                      <Fire size={16} />
                      <span className="font-medium">Data Destruction (DESTROY)</span>
                    </div>
                    <p className="text-sm text-zinc-500 mb-3">
                      Permanently destroy all R2 exports. This creates a TRUE_PURGED dead-end state.
                      Only use for legal/compliance requirements.
                    </p>
                    <div className="bg-red-500/10 border border-red-500/30 rounded p-2 mb-3 text-xs text-red-400">
                      WARNING: After destruction, you will need to use Repair Status to convert back to ARCHIVED for record keeping.
                    </div>
                    <Button
                      onClick={() => capabilities?.r2_enabled && setTruePurgeModal(true)}
                      disabled={!capabilities?.r2_enabled}
                      className={`${capabilities?.r2_enabled 
                        ? "bg-red-600 hover:bg-red-700 text-white" 
                        : "bg-zinc-700 text-zinc-400 cursor-not-allowed"}`}
                      data-testid="true-purge-btn"
                    >
                      <Fire size={16} className="mr-2" />
                      Destroy Data
                    </Button>
                    {!capabilities?.r2_enabled && (
                      <p className="text-sm text-amber-400 mt-2">
                        Data destruction is disabled because R2 is not configured in this environment.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* TRUE_PURGED: Show dead-end state with repair option */}
        {isTruePurged && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <Fire size={20} className="text-red-400" />
              <div>
                <h3 className="font-semibold text-red-400">Destroyed Tenant (TRUE_PURGED)</h3>
                <p className="text-sm text-zinc-500">
                  Data has been permanently destroyed. This is a dead-end state.
                </p>
              </div>
            </div>
            <div className="bg-zinc-900 rounded p-3 text-sm text-zinc-400">
              <p className="mb-2">To make this tenant manageable again:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Run Truth Check above</li>
                <li>Click Convert to Archived to preserve metadata</li>
                <li>Tenant will become a historical ARCHIVED record</li>
              </ol>
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal - SIMPLIFIED: Just "DELETE" */}
      {deleteModal && (
        <ConfirmModal
          title="Delete Tenant"
          message={`This will delete all ${deleteModal.preview?.inventory?.total_documents || 0} documents from ${tenant.company_name} and move to quarantine.`}
          warningLevel="warning"
          confirmText="DELETE"
          onConfirm={executeDelete}
          onCancel={() => setDeleteModal(null)}
          isProcessing={isDeleting}
          processingText="Deleting..."
        />
      )}
      
      {/* Restore Export Selection Modal */}
      {restoreModal && (
        <SelectExportModal
          exports={restoreModal.exports}
          onSelect={executeRestore}
          onCancel={() => setRestoreModal(null)}
          isProcessing={isRestoring}
        />
      )}
      
      {/* Archive Confirmation Modal */}
      {archiveModal && (
        <ConfirmModal
          title="Archive Tenant"
          message={`This will permanently archive ${tenant.company_name}. The tenant cannot be restored after archiving.`}
          warningLevel="info"
          confirmText="ARCHIVE"
          onConfirm={executeArchive}
          onCancel={() => setArchiveModal(false)}
          isProcessing={isArchiving}
          processingText="Archiving..."
        >
          <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3 mb-4 text-sm text-blue-400">
            <p><strong>What happens:</strong></p>
            <ul className="list-disc list-inside mt-1 text-zinc-400">
              <li>Export data retained indefinitely</li>
              <li>Tenant metadata preserved</li>
              <li>Restore will NOT be available</li>
              <li>Login permanently blocked</li>
            </ul>
          </div>
        </ConfirmModal>
      )}
      
      {/* Data Destruction Confirmation Modal - requires "DESTROY" */}
      {truePurgeModal && (
        <ConfirmModal
          title="Data Destruction"
          message={`This will PERMANENTLY DESTROY all data exports for ${tenant.company_name}.`}
          warningLevel="danger"
          confirmText="DESTROY"
          onConfirm={executeTruePurge}
          onCancel={() => setTruePurgeModal(false)}
          isProcessing={isTruePurging}
          processingText="Destroying..."
        >
          <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4 text-sm text-red-400">
            <p><strong>PERMANENT DESTRUCTION:</strong></p>
            <ul className="list-disc list-inside mt-1 text-zinc-400">
              <li>All R2 exports permanently deleted</li>
              <li>All export records removed</li>
              <li>No data recovery possible</li>
              <li>Tenant will be set to TRUE_PURGED (dead-end state)</li>
            </ul>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 mb-4 text-sm text-amber-400">
            <p><strong>After destruction:</strong></p>
            <p className="text-zinc-400 mt-1">
              Use Repair Status to convert TRUE_PURGED back to ARCHIVED for record keeping.
            </p>
          </div>
        </ConfirmModal>
      )}
    </div>
  );
}
